import React from 'react';
import '../styles/CompKanbanCard.css';
import { Condition, FinishChip, Ceiling } from './';
import { propertyConditionBG } from '../functions';
import { Image } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';

function CompKanbanCard({
    comp,
    propertyDocument,
    onClick,
    statusVariant = 'default',
    showScore = false,
    score = null,
    showCeiling = false,
    useInitialCondition = false
}) {
    const zpid = String(comp.uniqueIds?.zillowId || comp.zpid || '');
    const address = comp.addressDetails 
        ? `${comp.addressDetails.streetAddress || ''}, ${comp.addressDetails.city || ''}, ${comp.addressDetails.state || ''} ${comp.addressDetails.postalCode || ''}`.trim()
        : `ZPID: ${zpid}`;
    
    // Get condition - use initial condition if useInitialCondition is true (Phase 2), otherwise apply override from conditionUpdate
    let condition = comp.compDetails?.propertyCondition;
    let overrideCondition = null;
    
    if (!useInitialCondition) {
        overrideCondition = propertyDocument?.conditionUpdate?.find(c => {
            const entryZpid = String(c?.zpid || '');
            return entryZpid === zpid;
        });
        if (overrideCondition) {
            condition = overrideCondition.condition;
        }
    }

    // Get remodel tier
    let remodelTier = null;
    if (!useInitialCondition && overrideCondition?.remodelTier !== undefined && overrideCondition.remodelTier !== null) {
        remodelTier = overrideCondition.remodelTier;
    } else if (condition && typeof condition === 'object' && condition.remodelTier !== undefined) {
        remodelTier = condition.remodelTier;
    }

    // Determine condition label
    let conditionLabel = 'Unknown';
    if (typeof condition === 'string') {
        conditionLabel = condition;
    } else if (condition && typeof condition === 'object' && condition.condition) {
        conditionLabel = condition.condition;
    }

    // Determine card background based on status variant
    const cardClasses = ['comp-kanban-card'];
    if (statusVariant === 'condition-change') {
        cardClasses.push('comp-kanban-card-condition-change');
    } else if (statusVariant === 'eliminated') {
        cardClasses.push('comp-kanban-card-eliminated');
    } else if (statusVariant === 'expansion') {
        cardClasses.push('comp-kanban-card-expansion');
    }

    // Prepare FinishChip props if remodel tier exists
    let finishChip = null;
    const conditionStr = typeof conditionLabel === 'string' ? conditionLabel.toLowerCase() : '';
    if (remodelTier !== null && (conditionStr.includes('recently remodeled') || conditionStr.includes('modern build'))) {
        const tierStr = typeof remodelTier === 'number' 
            ? (remodelTier === 0 ? 'lipstick' : remodelTier === 1 ? 'middle' : 'luxury')
            : String(remodelTier).toLowerCase();
        
        const finishLabel = tierStr === 'middle' ? 'Mid-tier' : (tierStr === 'luxury' || tierStr === 'high') ? 'Luxury' : 'Lipstick';
        const finishLevel = tierStr === 'middle' ? 1 : (tierStr === 'luxury' || tierStr === 'high') ? 2 : 0;
        
        finishChip = <FinishChip label={finishLabel} level={finishLevel} />;
    }

    // Get first image from mlsPhotoUrls
    const propertyImages = comp.listingDetails?.mlsPhotoUrls;
    const firstImage = propertyImages && Array.isArray(propertyImages) && propertyImages.length > 0 
        ? propertyImages[0] 
        : null;

    return (
        <div 
            className={cardClasses.join(' ')}
            onClick={() => onClick && onClick(zpid)}
        >
            <div className="comp-kanban-card-media">
                <div className="comp-kanban-card-image">
                    {firstImage ? (
                        <img 
                            src={firstImage} 
                            alt={address}
                            className="comp-kanban-card-image-element"
                        />
                    ) : (
                        <div className="comp-kanban-card-no-image-container">
                            <Image
                                className="comp-kanban-card-no-image-element"
                                fill={colour.gray05}
                            />
                            <span className="label-regular colour-secondary block-text text-align-center">
                                No image
                            </span>
                        </div>
                    )}
                </div>
                {showScore && (
                    <div className="comp-kanban-card-score heading-small-bold colour-primary">
                        {score !== null && score !== undefined ? score : '—'}
                    </div>
                )}
            </div>
            <div className="comp-kanban-card-content">
                {showCeiling && (
                    <div className="comp-kanban-card-ceiling">
                        <Ceiling />
                    </div>
                )}
                <div className="comp-kanban-card-address body-regular colour-primary">
                    {address}
                </div>
                <div className="comp-kanban-card-zpid body-regular colour-secondary">
                    {zpid}
                </div>
                <div className="comp-kanban-card-status">
                    <div className="comp-kanban-card-condition">
                        <Condition
                            label={conditionLabel}
                            iconBG={propertyConditionBG(conditionLabel, false)}
                            darkMode={false}
                        />
                    </div>
                    {finishChip && (
                        <div className="comp-kanban-card-status-badge">
                            {finishChip}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CompKanbanCard;

